function setCartWalletPreference() {
  const userAgent = navigator.userAgent || '';
  const vendor = navigator.vendor || '';
  const isAppleMobile =
    /iPad|iPhone|iPod/.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isMac = /Macintosh|Mac OS X/.test(userAgent) || navigator.platform?.startsWith('Mac');
  const isSafari = /Safari/i.test(userAgent) && !/Chrome|Chromium|CriOS|FxiOS|Edg|OPR|Android/i.test(userAgent);
  const hasApplePaySession = typeof window.ApplePaySession !== 'undefined';
  let canMakeApplePayments = false;

  try {
    canMakeApplePayments =
      hasApplePaySession &&
      (typeof window.ApplePaySession.canMakePayments !== 'function' || window.ApplePaySession.canMakePayments());
  } catch {
    canMakeApplePayments = false;
  }

  const prefersApplePay =
    canMakeApplePayments || (isSafari && (hasApplePaySession || isAppleMobile || isMac || /Apple/i.test(vendor)));

  document.documentElement.classList.toggle('mb-wallet-prefers-apple', prefersApplePay);
  document.documentElement.classList.toggle('mb-wallet-prefers-google', !prefersApplePay);
}

// We hide Amazon, PayPal, and the non-native device wallet purely in CSS (keyed off
// the platform class set above), leaving Shop Pay + the device wallet. But Shopify's
// accelerated-checkout component reserves phantom vertical space for the hidden
// wallets and won't release it via CSS, so the two visible buttons end up separated
// by a tall gap. This stacks the VISIBLE buttons tight via transform and sets the
// container's exact height. It's idempotent — it backs out its own prior transform
// before re-measuring — so repeated calls converge instead of drifting.
function currentTranslateY(el) {
  const match = /translateY\((-?[\d.]+)px\)/.exec(el.style.transform || '');
  return match ? Number.parseFloat(match[1]) : 0;
}

function getCartDrawerExpressWalletContainers(root = document) {
  if (!root) return [];
  if (root.matches?.('.cart__dynamic-checkout-buttons')) return [root];

  // `renderContents` passes the <cart-drawer> element itself. Element#querySelectorAll
  // does not include that root when resolving the `.cart-drawer` ancestor in the
  // selector, so query relative to the drawer when that is our stable root.
  const selector = root.matches?.('cart-drawer, .cart-drawer')
    ? '.cart__dynamic-checkout-buttons'
    : '.cart-drawer .cart__dynamic-checkout-buttons';
  return Array.from(root.querySelectorAll?.(selector) || []);
}

const cartDrawerWalletButtonSelector =
  'shop-pay-wallet-button, shopify-shop-pay-button, shopify-apple-pay-button, shopify-google-pay-button';

function tidyCartDrawerExpressWallets(container) {
  const acc = container?.querySelector('shopify-accelerated-checkout-cart');
  if (!acc) return;
  const buttons = Array.from(acc.querySelectorAll(cartDrawerWalletButtonSelector)).filter(
    (button) => window.getComputedStyle(button).display !== 'none' && button.offsetHeight > 0,
  );
  if (!buttons.length) return;
  buttons.sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);

  const GAP = 9;
  const accTop = acc.getBoundingClientRect().top;
  // Pull the FIRST wallet to the container's top edge (not its own natural position).
  // The accelerated-checkout component places Shop Pay at a different internal offset
  // on fresh render (auto-open after add-to-cart) vs settled (reopen) — anchoring to
  // accTop makes the checkout→first-wallet gap consistent (set in CSS via the
  // express-checkout margin-top) regardless of that timing.
  let cursor = accTop;
  buttons.forEach((button) => {
    const rect = button.getBoundingClientRect();
    const naturalTop = rect.top - currentTranslateY(button);
    const delta = Math.round(cursor - naturalTop);
    button.style.transform = delta !== 0 ? `translateY(${delta}px)` : '';
    cursor += rect.height + GAP;
  });
  acc.style.setProperty('height', `${Math.round(cursor - GAP - accTop)}px`, 'important');
}

function scheduleCartDrawerExpressWalletTidy(container) {
  if (!container?.isConnected || container._mbExpressWalletTidyFrame) return;

  container._mbExpressWalletTidyFrame = window.requestAnimationFrame(() => {
    container._mbExpressWalletTidyFrame = null;
    if (!container.isConnected) return;

    tidyCartDrawerExpressWallets(container);

    const acc = container.querySelector('shopify-accelerated-checkout-cart');
    if (!acc) return;

    const mutationObserver = container._mbExpressWalletMutationObserver;
    if (mutationObserver && container._mbExpressWalletMutationTarget !== acc) {
      mutationObserver.disconnect();
      mutationObserver.observe(acc, { childList: true, subtree: true });
      container._mbExpressWalletMutationTarget = acc;
    }

    const resizeObserver = container._mbExpressWalletResizeObserver;
    if (!resizeObserver) return;
    const resizeTargets =
      container._mbExpressWalletResizeTargets || (container._mbExpressWalletResizeTargets = new WeakSet());

    // Observe wallet hosts themselves. Their rendered height changes when a
    // device wallet becomes available, while our own accelerated-checkout height
    // update cannot recursively trigger this observer.
    acc.querySelectorAll(cartDrawerWalletButtonSelector).forEach((button) => {
      if (resizeTargets.has(button)) return;
      resizeTargets.add(button);
      resizeObserver.observe(button);
    });
  });
}

function observeCartDrawerExpressWallets(container) {
  if (!container) return;

  if (!container._mbExpressWalletMutationObserver && typeof MutationObserver !== 'undefined') {
    container._mbExpressWalletMutationObserver = new MutationObserver((mutations) => {
      const walletSelector = cartDrawerWalletButtonSelector;
      const walletsChanged = mutations.some((mutation) =>
        [...mutation.addedNodes, ...mutation.removedNodes].some(
          (node) =>
            node.nodeType === Node.ELEMENT_NODE &&
            (node.matches?.(walletSelector) || node.querySelector?.(walletSelector)),
        ),
      );

      if (walletsChanged) scheduleCartDrawerExpressWalletTidy(container);
    });
  }

  if (!container._mbExpressWalletResizeObserver && typeof ResizeObserver !== 'undefined') {
    container._mbExpressWalletResizeTargets = new WeakSet();
    container._mbExpressWalletResizeObserver = new ResizeObserver(() => {
      scheduleCartDrawerExpressWalletTidy(container);
    });
  }

  scheduleCartDrawerExpressWalletTidy(container);
}

function disconnectCartDrawerExpressWalletObservers(root = document) {
  const containers = getCartDrawerExpressWalletContainers(root);

  containers.forEach((container) => {
    container._mbExpressWalletMutationObserver?.disconnect();
    container._mbExpressWalletResizeObserver?.disconnect();

    if (container._mbExpressWalletTidyFrame) {
      window.cancelAnimationFrame(container._mbExpressWalletTidyFrame);
    }

    delete container._mbExpressWalletMutationObserver;
    delete container._mbExpressWalletMutationTarget;
    delete container._mbExpressWalletResizeObserver;
    delete container._mbExpressWalletResizeTargets;
    delete container._mbExpressWalletTidyFrame;
  });
}

function prepareCartDrawerExpressWallets(root = document) {
  setCartWalletPreference();
  const containers = getCartDrawerExpressWalletContainers(root);

  containers.forEach((container) => {
    tidyCartDrawerExpressWallets(container);
    observeCartDrawerExpressWallets(container);
  });
}

function syncCartDrawerExpressWallets(root = document) {
  prepareCartDrawerExpressWallets(root);
  // Shopify can replace the entire express-checkout block after a section refresh.
  // Re-query from the stable drawer root instead of retrying a detached container.
  [200, 600, 1500, 3000, 6000, 10000].forEach((delay) => {
    window.setTimeout(() => prepareCartDrawerExpressWallets(root), delay);
  });
}

// The native Shopify Bundles app injects a "Show / Hide N items" component breakdown
// into bundle cart lines and defaults it to EXPANDED (aria-expanded="true"). We collapse
// it by default — once per toggle — so the cart stays compact (room for upsells).
// Shoppers can still expand it manually; the data-attr guard means we never re-collapse
// after they do. The app re-injects fresh (unmarked) toggles on each cart re-render.
function collapseCartBundlesByDefault() {
  document
    .querySelectorAll('.cart-drawer .bundle-toggle-btn[aria-expanded="true"]:not([data-mb-bundle-collapsed])')
    .forEach((btn) => {
      btn.setAttribute('data-mb-bundle-collapsed', '1');
      try {
        if (typeof window.toggleBundleComponents === 'function') window.toggleBundleComponents(btn);
        else btn.click();
      } catch (e) {
        /* no-op */
      }
    });
}

function scheduleCartBundleCollapse() {
  collapseCartBundlesByDefault();
  // The app injects/repaints the breakdown asynchronously; re-check a few times.
  [120, 400, 1000, 2500].forEach((delay) => window.setTimeout(collapseCartBundlesByDefault, delay));
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // Track input modality so close() can avoid leaving a focus ring on the cart
    // icon after a pointer/touch dismissal (the drawer returns focus to it, and
    // browsers treat that programmatic focus as :focus-visible).
    this.lastInputWasPointer = false;
    document.addEventListener('pointerdown', () => { this.lastInputWasPointer = true; }, true);
    document.addEventListener('keydown', () => { this.lastInputWasPointer = false; }, true);

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('submit', this.onSetupSubmit.bind(this));
    this.setHeaderCartIconAccessibility();
    this.sortCartItems();
    syncCartDrawerExpressWallets(this);
    scheduleCartBundleCollapse();
  }

  async onSetupSubmit(event) {
    const form = event.target.closest('.cart-drawer__setup-form');
    if (!form) return;

    event.preventDefault();

    const submitButton = form.querySelector('[type="submit"]');
    if (submitButton?.getAttribute('aria-disabled') === 'true') return;

    const config = fetchConfig();
    config.headers['X-Requested-With'] = 'XMLHttpRequest';

    const formData = new FormData(form);
    const item = {
      id: Number(formData.get('id')),
      quantity: Number(formData.get('quantity') || 1),
    };

    config.body = JSON.stringify({
      items: [item],
      sections: this.getSectionsToRender().map((section) => section.id).join(','),
      sections_url: window.location.pathname,
    });

    this.setActiveElement(document.activeElement);
    submitButton?.setAttribute('aria-disabled', true);
    submitButton?.classList.add('loading');

    const addUrl = new URL(routes.cart_add_url || '/cart/add', window.location.origin);
    if (!addUrl.pathname.endsWith('.js')) addUrl.pathname = `${addUrl.pathname}.js`;

    try {
      const response = await fetch(addUrl.toString(), config);
      const responseText = await response.text();
      let parsedResponse = null;

      try {
        parsedResponse = JSON.parse(responseText);
      } catch {
        parsedResponse = null;
      }

      if (!response.ok || parsedResponse?.status) {
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = parsedResponse?.description || parsedResponse?.message || window.cartStrings.error;
        return;
      }

      await this.refreshContents();
    } catch {
      const errors = document.getElementById('CartDrawer-CartErrors');
      if (errors) errors.textContent = window.cartStrings.error;
    } finally {
      submitButton?.classList.remove('loading');
      submitButton?.removeAttribute('aria-disabled');
    }
  }

  async refreshContents() {
    const sectionIds = this.getSectionsToRender().map((section) => section.id);
    const sectionsUrl = new URL(window.location.pathname, window.location.origin);
    sectionsUrl.searchParams.set('sections', sectionIds.join(','));

    const cartUrl = new URL(routes.cart_url || '/cart', window.location.origin);
    if (!cartUrl.pathname.endsWith('.js')) cartUrl.pathname = `${cartUrl.pathname}.js`;

    const [cartResponse, sectionsResponse] = await Promise.all([
      fetch(cartUrl.toString(), {
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      }),
      fetch(sectionsUrl.toString(), {
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      }),
    ]);

    if (!cartResponse.ok || !sectionsResponse.ok) throw new Error('Unable to refresh cart drawer');

    const cartState = await cartResponse.json();
    const sections = await sectionsResponse.json();
    const errors = document.getElementById('CartDrawer-CartErrors');
    if (errors) errors.textContent = '';
    this.renderContents({ ...cartState, sections });
  }

  sortCartItems() {
    const tbody = this.querySelector('#CartDrawer-CartItems tbody');
    if (!tbody) return;

    const rows = Array.from(tbody.querySelectorAll('.cart-item'));
    if (rows.length < 2) return;

    const relationshipContract = window.MagBakCartRelationship;
    const sortableRows = rows.map((row, index) => {
      const parsed = relationshipContract?.safeParseLink(row.dataset.mbLink || '');
      return {
        row,
        index,
        group: row.dataset.cartGroup || '',
        order: Number.parseInt(row.dataset.cartOrder || index, 10),
        groupTime: Number.parseInt((row.dataset.cartGroup || '').split('-')[0], 10),
        line: Number.parseInt(row.dataset.cartLine || index, 10),
        relation: parsed?.ok ? parsed.link : null,
      };
    });
    const relationByGroupNode = new Map(sortableRows
      .filter((item) => item.relation)
      .map((item) => [`${item.relation.group}:${item.relation.node}`, item.relation]));
    const relationshipPath = (relation) => {
      if (!relation) return [];
      const path = [relation.node];
      const visited = new Set([relation.node]);
      let parent = relation.parent;
      while (parent && !visited.has(parent)) {
        path.unshift(parent);
        visited.add(parent);
        parent = relationByGroupNode.get(`${relation.group}:${parent}`)?.parent || null;
      }
      return path.map((node) => Number.parseInt(node, 36));
    };
    sortableRows.forEach((item) => { item.relationshipPath = relationshipPath(item.relation); });
    const comparePaths = (a, b) => {
      const length = Math.max(a.length, b.length);
      for (let index = 0; index < length; index += 1) {
        if (a[index] === undefined) return -1;
        if (b[index] === undefined) return 1;
        if (a[index] !== b[index]) return a[index] - b[index];
      }
      return 0;
    };

    const orderedRows = sortableRows
      .sort((a, b) => {
        if (a.relation && b.relation && a.relation.group === b.relation.group) {
          return comparePaths(a.relationshipPath, b.relationshipPath) || a.line - b.line;
        }
        if (a.group && b.group && a.group === b.group) return a.order - b.order;
        if (a.group && b.group && a.group !== b.group) {
          const groupDelta = a.groupTime - b.groupTime;
          return Number.isFinite(groupDelta) && groupDelta !== 0 ? groupDelta : a.line - b.line;
        }
        return a.line - b.line;
      })
      .map((item) => item.row);

    orderedRows.forEach((row) => tbody.appendChild(row));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    syncCartDrawerExpressWallets(this);
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    // Keyboard dismissals (Esc/Enter) keep focus on the cart icon for a11y; pointer
    // dismissals shouldn't leave its focus ring lingering until the next tap.
    if (this.lastInputWasPointer && this.activeElement?.blur) this.activeElement.blur();
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const hasItems =
      typeof parsedState.item_count === 'number'
        ? parsedState.item_count > 0
        : Boolean(parsedState.id || parsedState.items?.length);

    disconnectCartDrawerExpressWalletObservers(this);
    this.classList.toggle('is-empty', !hasItems);
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !parsedState.sections?.[section.id]) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    this.sortCartItems();
    syncCartDrawerExpressWallets(this);
    scheduleCartBundleCollapse();
    window.mbRefreshRedesignCartIcon?.();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  renderCartUpdate(...args) {
    super.renderCartUpdate(...args);
    // Re-sync the express wallets after the base class re-renders the drawer, so the
    // rebuilt Apple/Google Pay button re-aligns instead of staying hidden.
    // IMPORTANT: defer + guard. renderCartUpdate runs inside the cart fetch's .then,
    // so a throw here would hit its .catch and show "error updating your cart" even
    // though the cart updated fine. rAF moves it out of that chain; try/catch is a
    // belt-and-suspenders so a re-sync hiccup is never user-visible.
    requestAnimationFrame(() => {
      try {
        syncCartDrawerExpressWallets(document);
      } catch (e) {
        /* non-fatal — the layout re-settles on the next open / observer tick */
      }
    });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Discount-code entry for the cart drawer. Best-practice pattern (Baymard/NN-g):
// collapsed behind a "Have a code?" link so no-code shoppers aren't primed to
// coupon-hunt, applied via /cart/update.js so the price reflects pre-checkout
// (and flows to Shop Pay / Apple Pay). Re-renders from the sections returned by
// that same request (the proven single-request path the qty stepper uses), with
// a hard reload fallback so apply/remove can never silently hang.
class CartDiscount extends HTMLElement {
  connectedCallback() {
    this.toggleBtn = this.querySelector('[data-discount-toggle]');
    this.panel = this.querySelector('[data-discount-panel]');
    this.input = this.querySelector('[data-discount-input]');
    this.applyBtn = this.querySelector('[data-discount-apply]');
    this.removeBtn = this.querySelector('[data-discount-remove]');
    this.errorEl = this.querySelector('[data-discount-error]');

    this.toggleBtn?.addEventListener('click', () => this.togglePanel());
    this.applyBtn?.addEventListener('click', () => this.apply());
    this.removeBtn?.addEventListener('click', () => this.removeCode());
    this.input?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.apply();
      }
    });
    this.input?.addEventListener('input', () => this.clearError());

    this.maybeAutoApply();
  }

  get updateUrl() {
    const base = (window.routes && window.routes.cart_update_url) || '/cart/update';
    return base.endsWith('.js') ? base : `${base}.js`;
  }

  get cartUrl() {
    const base = (window.routes && window.routes.cart_url) || '/cart';
    return base.endsWith('.js') ? base : `${base}.js`;
  }

  togglePanel() {
    if (!this.panel) return;
    const willOpen = this.panel.hasAttribute('hidden');
    this.panel.toggleAttribute('hidden', !willOpen);
    this.toggleBtn?.setAttribute('aria-expanded', String(willOpen));
    if (willOpen) this.input?.focus();
  }

  async postDiscount(value) {
    const drawer = document.querySelector('cart-drawer');
    const sectionIds =
      drawer && typeof drawer.getSectionsToRender === 'function'
        ? drawer.getSectionsToRender().map((s) => s.id)
        : [];
    const body = { discount: value };
    if (sectionIds.length) {
      // Ask /cart/update.js to return the re-rendered sections in ONE request —
      // the same mechanism the qty stepper uses, so the drawer repaints reliably.
      body.sections = sectionIds;
      body.sections_url = window.location.pathname;
    }
    const res = await fetch(this.updateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    return res.json();
  }

  async fetchCart() {
    const res = await fetch(this.cartUrl, {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!res.ok) throw new Error('Cart request failed');
    return res.json();
  }

  // Invalid codes still return HTTP 200 — the truth is the `applicable` flag.
  codeApplied(cart, code) {
    const c = code.trim().toLowerCase();
    if (Array.isArray(cart.discount_codes)) {
      return cart.discount_codes.some((d) => (d.code || '').toLowerCase() === c && d.applicable !== false);
    }
    return (cart.cart_level_discount_applications || []).length > 0 || (cart.total_discount || 0) > 0;
  }

  bundleLineKeys(cart) {
    const items = cart?.items || [];
    const contract = window.MagBakCartRelationship;
    if (contract) {
      const graph = contract.buildGraph(items.map((item) => ({
        key: item.key,
        quantity: Number(item.quantity || 0),
        properties: item.properties || {},
      })));
      const keys = new Set();
      graph.groups.forEach((relations) => {
        if (!relations.some((relation) => relation.kind === contract.KINDS.BUNDLE_PEER)) return;
        relations.forEach((relation) => keys.add(relation.lineKey));
      });
      if (keys.size) return keys;
    }
    return new Set(items
      .filter((item) => {
        const properties = item?.properties || {};
        return properties._bundle_key
          || properties._mb_bundle_note
          || properties._mb_bb_parent
          || properties._mb_bb_role;
      })
      .map((item) => item.key));
  }

  cartHasBundle(cart) {
    return this.bundleLineKeys(cart).size > 0;
  }

  cartHasStackableTeslaPair(cart) {
    // Fixed-price native pairs allow additional eligible discounts. Shopify
    // remains responsible for each code's eligibility and combinations.
    return (cart?.items || []).some((item) =>
      item.handle === 'magbak-tesla-25w-left-right-bundle'
      && item.has_components === true);
  }

  cartHasRegularItems(cart) {
    const bundleKeys = this.bundleLineKeys(cart);
    return (cart?.items || []).some((item) => !bundleKeys.has(item.key));
  }

  domCartHasBundle() {
    return !!document.querySelector(
      'cart-drawer .cart-item[data-mb-link$=".b"], cart-drawer .cart-item__bundle-note'
    );
  }

  bundleCodeMessage(cart) {
    if (this.cartHasRegularItems(cart)) {
      return "Promo codes can't be combined with bundle savings. To use a promo code on regular items, remove the bundle or check out separately.";
    }
    return "Promo codes can't be combined with bundle savings. Your bundle discount is already applied.";
  }

  invalidCodeMessage(cart) {
    if (this.cartHasBundle(cart) && !this.cartHasStackableTeslaPair(cart)) {
      return this.bundleCodeMessage(cart);
    }
    return "That code isn't valid or has expired.";
  }

  // Re-render from the cart state returned by /cart/update.js (which already
  // includes the rendered `sections`). No second fetch. If sections are missing
  // or rendering throws, hard-reload so the drawer can never get stuck mid-update.
  renderFromState(state) {
    try {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && state && state.sections && typeof drawer.renderContents === 'function') {
        drawer.renderContents(state);
        return;
      }
    } catch (e) {
      /* fall through to reload */
    }
    window.location.reload();
  }

  setBusy(busy) {
    this.classList.toggle('cart-discount--busy', busy);
    this.applyBtn?.toggleAttribute('aria-disabled', busy);
    this.removeBtn?.toggleAttribute('aria-disabled', busy);
    this.input?.toggleAttribute('disabled', busy);
  }

  showError(msg) {
    if (!this.errorEl) return;
    this.errorEl.textContent = msg;
    this.errorEl.hidden = false;
  }

  clearError() {
    if (!this.errorEl) return;
    this.errorEl.textContent = '';
    this.errorEl.hidden = true;
  }

  async apply() {
    const code = (this.input?.value || '').trim();
    if (!code) {
      this.input?.focus();
      return;
    }
    this.clearError();
    this.setBusy(true);
    try {
      let currentCart = null;
      try {
        currentCart = await this.fetchCart();
      } catch (e) {
        /* fall back to current drawer markup below */
      }
      if (!this.cartHasStackableTeslaPair(currentCart) && (this.cartHasBundle(currentCart) || (!currentCart && this.domCartHasBundle()))) {
        this.showError(this.bundleCodeMessage(currentCart));
        this.setBusy(false);
        return;
      }

      const state = await this.postDiscount(code);
      if (!this.codeApplied(state, code)) {
        await this.postDiscount(''); // clear the rejected code so nothing lingers on the cart
        this.showError(this.invalidCodeMessage(state));
        this.setBusy(false);
        return; // no re-render — keep the input + error visible
      }
      this.renderFromState(state); // success → repaint into the "applied" state + re-sync wallets
    } catch (e) {
      this.showError("Couldn't apply the code. Please try again.");
      this.setBusy(false);
    }
  }

  async removeCode() {
    this.setBusy(true);
    try {
      const state = await this.postDiscount('');
      this.renderFromState(state);
    } catch (e) {
      window.location.reload(); // never leave the drawer stuck
    }
  }

  // Auto-apply from a ?discount=CODE link (email/SMS/ads) — the lowest-friction
  // pattern: code-holders never see an empty field. Runs once per page load.
  async maybeAutoApply() {
    if (window.__mbDiscountAutoApplied) return;
    let params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return;
    }
    const code = params.get('discount');
    if (!code) return;
    window.__mbDiscountAutoApplied = true;
    // strip the param so a re-render can't re-trigger it and the URL stays clean
    params.delete('discount');
    const qs = params.toString();
    try {
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
    } catch (e) {
      /* non-fatal */
    }
    try {
      const state = await this.postDiscount(code);
      if (!this.codeApplied(state, code)) {
        await this.postDiscount('');
        return;
      }
      this.renderFromState(state);
      document.querySelector('cart-drawer')?.open?.(); // reveal the applied savings
    } catch (e) {
      /* non-fatal */
    }
  }
}

customElements.define('cart-discount', CartDiscount);

syncCartDrawerExpressWallets();
